import { useEffect, useMemo, useState } from "react";

import type {
  AgentGroundedAnswer,
  PolyfutureApiClient,
  ResearchResult,
  ResearchResultSummary,
} from "./api.js";
import { CitationChip, ResearchFigure } from "./Agent.js";
import type { PlatformRole } from "./types.js";

export interface PortfolioChoice {
  readonly id: string;
  readonly name: string;
}

/** The default title: the question on one line, short enough to scan in a list. */
export function researchTitle(question: string): string {
  const oneLine = question.replace(/\s+/g, " ").trim();
  return oneLine.length <= 80 ? oneLine : `${oneLine.slice(0, 79).trimEnd()}…`;
}

/**
 * ADR-091. "Save to Research" under an answer. The server copies the stored answer; this sends
 * only the answer id, the portfolio and the title. With one portfolio there is no choice to make.
 */
export function SaveToResearch({
  client,
  role,
  answer,
  question,
  portfolios,
  defaultPortfolioId,
  onWatch,
}: {
  readonly client: PolyfutureApiClient;
  readonly role: PlatformRole;
  readonly answer: AgentGroundedAnswer;
  readonly question: string;
  readonly portfolios: readonly PortfolioChoice[];
  readonly defaultPortfolioId?: string | undefined;
  readonly onWatch: (result: ResearchResult) => void;
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(() => researchTitle(question));
  const [portfolioId, setPortfolioId] = useState(
    () =>
      portfolios.find((portfolio) => portfolio.id === defaultPortfolioId)?.id ??
      portfolios.at(0)?.id ??
      "",
  );
  const [state, setState] = useState<"idle" | "saving" | "failed">("idle");
  const [saved, setSaved] = useState<ResearchResult>();
  const canSave = role !== "viewer";

  if (portfolios.length === 0) return null;
  if (saved !== undefined) {
    return (
      <div className="research-save research-save-done" role="status">
        <p>
          <strong>✓ Saved to Research</strong> · {saved.bookName}. Confirmed by the server. It is in
          Market research → Saved results.
        </p>
        <button className="button" type="button" onClick={() => onWatch(saved)}>
          Watch with an alert rule
        </button>
      </div>
    );
  }
  if (!open) {
    return (
      <div className="research-save">
        <button className="button" type="button" disabled={!canSave} onClick={() => setOpen(true)}>
          Save to Research
        </button>
        {canSave ? null : <small>Saving needs the analyst or admin role.</small>}
      </div>
    );
  }
  return (
    <form
      className="research-save research-save-form"
      aria-label="Save to Research"
      onSubmit={(event) => {
        event.preventDefault();
        if (title.trim().length === 0 || portfolioId === "") return;
        setState("saving");
        void client
          .saveResearchResult({
            answerId: answer.answerId,
            bookId: portfolioId,
            title: title.trim(),
          })
          .then(
            (result) => {
              setSaved(result);
              setState("idle");
            },
            () => setState("failed"),
          );
      }}
    >
      <label>
        <span>Title</span>
        <input
          value={title}
          maxLength={200}
          onChange={(event) => setTitle(event.target.value)}
          required
        />
      </label>
      {portfolios.length > 1 ? (
        <label>
          <span>Portfolio</span>
          <select value={portfolioId} onChange={(event) => setPortfolioId(event.target.value)}>
            {portfolios.map((portfolio) => (
              <option value={portfolio.id} key={portfolio.id}>
                {portfolio.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <div className="research-save-actions">
        <button
          className="button button-primary"
          type="submit"
          disabled={state === "saving" || title.trim().length === 0}
        >
          {state === "saving" ? "Saving…" : "Save to Research"}
        </button>
        <button className="button" type="button" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
      {state === "failed" ? (
        <p role="alert" className="research-save-error">
          The server did not confirm, so nothing was saved. Try again.
        </p>
      ) : null}
    </form>
  );
}

/** Market research → Saved results: research results from every portfolio (V-A5, V-S1d). */
export function SavedResearchResults({
  client,
  onWatch,
}: {
  readonly client: PolyfutureApiClient;
  readonly onWatch: (result: ResearchResult) => void;
}) {
  const [results, setResults] = useState<readonly ResearchResultSummary[]>();
  const [error, setError] = useState<string>();
  const [portfolioFilter, setPortfolioFilter] = useState("");
  const [openId, setOpenId] = useState<string>();

  useEffect(() => {
    let current = true;
    void client.listResearchResults().then(
      (listed) => {
        if (current) setResults(listed);
      },
      (caught: unknown) => {
        if (current)
          setError(caught instanceof Error ? caught.message : "Saved results did not load.");
      },
    );
    return () => {
      current = false;
    };
  }, [client]);

  const portfolios = useMemo(() => {
    const byId = new Map<string, string>();
    for (const result of results ?? []) byId.set(result.bookId, result.bookName);
    return [...byId.entries()].map(([id, name]) => ({ id, name }));
  }, [results]);
  const shown = (results ?? []).filter(
    (result) => portfolioFilter === "" || result.bookId === portfolioFilter,
  );

  if (openId !== undefined) {
    return (
      <ResearchResultDetail
        client={client}
        id={openId}
        onBack={() => setOpenId(undefined)}
        onWatch={onWatch}
      />
    );
  }
  return (
    <section className="panel research-results" aria-labelledby="research-results-heading">
      <header className="research-results-head">
        <h2 id="research-results-heading">Saved research results</h2>
        {portfolios.length > 1 ? (
          <label>
            <span>Portfolio</span>
            <select
              value={portfolioFilter}
              onChange={(event) => setPortfolioFilter(event.target.value)}
            >
              <option value="">All portfolios</option>
              {portfolios.map((portfolio) => (
                <option value={portfolio.id} key={portfolio.id}>
                  {portfolio.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </header>
      {error !== undefined ? (
        <p role="alert">{error}</p>
      ) : results === undefined ? (
        <p role="status">Loading saved results…</p>
      ) : shown.length === 0 ? (
        <p>
          Nothing saved yet. Ask Polyfuture a research question, then choose “Save to Research”
          under the answer.
        </p>
      ) : (
        <div className="research-results-table" tabIndex={0}>
          <table>
            <thead>
              <tr>
                <th scope="col">Result</th>
                <th scope="col">Portfolio</th>
                <th scope="col">Facts</th>
                <th scope="col">Sources</th>
                <th scope="col">Saved</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((result) => (
                <tr key={result.id}>
                  <td>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => setOpenId(result.id)}
                    >
                      {result.title}
                    </button>
                  </td>
                  <td>{result.bookName}</td>
                  <td>{result.facts}</td>
                  <td>{result.sources}</td>
                  <td>
                    <time dateTime={result.savedAt}>
                      {new Date(result.savedAt).toLocaleDateString()}
                    </time>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ResearchResultDetail({
  client,
  id,
  onBack,
  onWatch,
}: {
  readonly client: PolyfutureApiClient;
  readonly id: string;
  readonly onBack: () => void;
  readonly onWatch: (result: ResearchResult) => void;
}) {
  const [result, setResult] = useState<ResearchResult>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    let current = true;
    void client.getResearchResult(id).then(
      (loaded) => {
        if (current) setResult(loaded);
      },
      (caught: unknown) => {
        if (current)
          setError(caught instanceof Error ? caught.message : "The result did not load.");
      },
    );
    return () => {
      current = false;
    };
  }, [client, id]);
  const sources = useMemo(
    () => (result === undefined ? undefined : { claims: result.facts, citations: result.sources }),
    [result],
  );
  return (
    <section className="panel research-result-detail" aria-labelledby="research-result-title">
      <button className="back-button" type="button" onClick={onBack}>
        ← Saved results
      </button>
      {error !== undefined ? (
        <p role="alert">{error}</p>
      ) : result === undefined || sources === undefined ? (
        <p role="status">Loading…</p>
      ) : (
        <>
          <p className="research-result-tag">Research · saved</p>
          <h2 id="research-result-title">{result.title}</h2>
          <p className="research-result-meta">
            {result.bookName} · answered {new Date(result.answeredAt).toLocaleString()} · saved{" "}
            {new Date(result.savedAt).toLocaleString()}
          </p>
          <blockquote className="research-result-question">{result.question}</blockquote>
          <p>{result.answer}</p>
          {result.facts.length === 0 ? null : (
            <section aria-labelledby="research-result-facts">
              <h3 id="research-result-facts">Facts</h3>
              <ul>
                {result.facts.map((fact) => (
                  <li key={fact.claimId}>{fact.text}</li>
                ))}
              </ul>
            </section>
          )}
          {result.figures.length === 0 ? null : (
            <div className="agent-generated-grid">
              {result.figures.map((figure) => (
                <ResearchFigure component={figure} sources={sources} key={figure.componentId} />
              ))}
            </div>
          )}
          {result.limitations.length === 0 ? null : (
            <section aria-labelledby="research-result-limits">
              <h3 id="research-result-limits">Limitations</h3>
              <ul>
                {result.limitations.map((limitation) => (
                  <li key={limitation.limitationId}>{limitation.message}</li>
                ))}
              </ul>
            </section>
          )}
          <section aria-labelledby="research-result-sources">
            <h3 id="research-result-sources">Source details</h3>
            {result.sources.length === 0 ? (
              <p>This answer cited no sources.</p>
            ) : (
              <div className="agent-citation-list">
                {result.sources.map((citation) => (
                  <CitationChip citation={citation} key={citation.citationId} />
                ))}
              </div>
            )}
          </section>
          <div className="research-save-actions">
            <button className="button" type="button" onClick={() => onWatch(result)}>
              Watch with an alert rule
            </button>
          </div>
        </>
      )}
    </section>
  );
}
